import type { VehicleReview } from '../api/types'
import { formatLongDate } from '../domain/format'

/**
 * The published road test: the headline and summary with the model photo beside them, then
 * "Pluspunten" and "Minpunten" side by side with green `+` and red `−` markers, and the link out.
 *
 * Two things here are obligations rather than styling, and both come from the API's contract:
 *
 * - **Attribution is not optional.** `source` and `url` are `required` on the backend record
 *   precisely so a client cannot render the review without naming the publisher and linking back.
 *   Both live in the one link at the foot, "Lees de volledige review op {source}": keep the
 *   publisher in its text, and do not make the link conditional or drop it to save space.
 * - **This is about the model, not this car.** A review published in 2011 describes the model as it
 *   was then; the car in front of the reader may be a later facelift. The line under the heading
 *   says so, because presenting editorial opinion as registration data is the one way this section
 *   could mislead. It names what the review is not about as `de staat van deze auto`: a bare "niet
 *   over deze specifieke auto" reads as an argument against the card being here at all, where the
 *   condition of the car is the thing a reader would actually have taken from it wrongly.
 */
export function ReviewSection({ review }: { review: VehicleReview }) {
  const published = formatLongDate(review.publishedOn)
  const pros = review.pros ?? []
  const cons = review.cons ?? []

  return (
    <div className="review">
      <p className="review__scope">
        Deze beoordeling{published ? ` van ${published}` : ''} gaat over het model
        {review.subject ? <strong> {review.subject}</strong> : ''} in het algemeen, niet over de
        staat van deze auto.
      </p>

      {/*
        The verdict and the picture side by side, the same three-to-two split and the same
        breakpoint as the report header at the top of the page. Two reasons beyond the height it
        saves: it is the same photograph in both places, so arranging it the same way is what makes
        a reader read it as "the model shot" rather than as a second picture, and stacking a 512px
        photo above three lines of summary would spend most of the card on one press image.

        Split only when there is something to put beside the photo. A review with a picture and no
        prose would otherwise render a half-width photo against an empty column.
      */}
      <div className={`review__intro${review.photoUrl && (review.title || review.summary) ? ' review__intro--split' : ''}`}>
        <div>
          {review.title && <h3 className="review__headline">{review.title}</h3>}

          {review.summary && (
            <>
              <h4 className="data-subheading">Samenvatting</h4>
              <p className="review__summary">{review.summary}</p>
            </>
          )}
        </div>

        {review.photoUrl && (
          <img
            className="review__photo"
            src={review.photoUrl}
            alt={review.subject ? `Foto van een ${review.subject}` : 'Foto van het beoordeelde model'}
            loading="lazy"
            // The photo is hosted by the publisher, so it says nothing about who is reading it.
            referrerPolicy="no-referrer"
          />
        )}
      </div>

      {(pros.length > 0 || cons.length > 0) && (
        <div className="review__verdict">
          <VerdictList tone="pros" title="Pluspunten" items={pros} />
          <VerdictList tone="cons" title="Minpunten" items={cons} />
        </div>
      )}

      <p className="review__attribution">
        <a
          className="review__link"
          href={review.url}
          target="_blank"
          // nofollow because this is an editorial credit, not an endorsement we are passing rank to.
          rel="nofollow noreferrer noopener"
        >
          Lees de volledige review op {review.source}
        </a>
      </p>
    </div>
  )
}

function VerdictList({ tone, title, items }: { tone: 'pros' | 'cons'; title: string; items: string[] }) {
  if (items.length === 0) {
    return null
  }

  return (
    <div>
      <p className={`review__verdict-title review__verdict-title--${tone}`}>{title}</p>
      <ul className={`review__list review__list--${tone}`}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  )
}
